<template>
  <div class="admin-account-analytics">
    <div class="d-flex justify-content-end mb-3">
      <analytics-date-range-picker
        v-model="dateRange"
        :min-date="minDate"
        @input="onDateRangeChange"
      />
    </div>

    <!-- KPIs -->
    <div v-if="queryErrors.globalStats" class="query-error mb-4">
      {{ $t('settings.analytics.globalStatsQueryError') }}
    </div>
    <b-row v-else-if="globalStats" class="global-stats mb-4">
      <b-col cols="6" sm="3">
        <h5>{{ $t('settings.analytics.visitors') }}</h5>
        <div>{{ globalStats.visitors }}</div>
      </b-col>
      <b-col cols="6" sm="3">
        <h5>{{ $t('settings.analytics.views') }}</h5>
        <div>{{ globalStats.views }}</div>
      </b-col>
      <b-col cols="6" sm="3">
        <h5>{{ $t('settings.analytics.avgTime') }}</h5>
        <div>{{ globalStats.avgTime | timeString }}</div>
      </b-col>
      <b-col cols="6" sm="3">
        <h5>{{ $t('settings.analytics.bounceRate') }}</h5>
        <div>{{ globalStats.bounceRate }}</div>
      </b-col>
    </b-row>

    <!-- Reads-per-day chart -->
    <div class="reads-per-day">
      <h4>{{ $t('settings.analytics.readsPerDay') }}</h4>
      <div v-if="queryErrors.readsPerDayObject" class="query-error">
        {{ $t('settings.analytics.readsPerDayQueryError') }}
      </div>
      <line-chart
        v-else-if="readsPerDayObject && readsPerDayObject.length"
        :key="readsPerDayKey"
        :reads-per-day-object="readsPerDayObject"
        :date-range="dateRange"
      />
      <p v-else>{{ $t('settings.analytics.noReadsPerDay') }}</p>
    </div>
  </div>
</template>

<script>
import AnalyticsDateRangePicker from '@/components/settings/analytics/AnalyticsDateRangePicker.vue';
import LineChart from '@/components/settings/analytics/LineChart.vue';
import SetLoadingIndicator from '@/graphQlQueries/mutations/setLoadingIndicatorInCache';
import {
  MIN_DATE,
  isRangeInPirsch,
  formatTimeString,
  getGlobalStats,
  getReadsPerDay,
  fetchGlobalStats,
  fetchSessionDuration,
  fetchReadsPerDay,
} from '@/components/settings/analytics/pirschStats';

export default {
  name: 'AdminAccountAnalytics',
  components: { AnalyticsDateRangePicker, LineChart },
  props: {
    pirschCode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      dateRange: { startDate: null, endDate: null },
      minDate: MIN_DATE.toISOString(),
      globalStats: null,
      readsPerDayObject: null,
      readsPerDayKey: 0,
      queryErrors: {
        globalStats: false,
        readsPerDayObject: false,
      },
    };
  },
  created() {
    this.initializeDates();
  },
  mounted() {
    // Component is only mounted when the admin expands the analytics section,
    // so fetching here keeps the modal open cheap (lazy on-demand).
    this.fetchStats();
  },
  methods: {
    initializeDates() {
      const endDate = new Date();
      endDate.setHours(23, 59, 59, 0);
      this.dateRange.endDate = endDate.toISOString();

      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setHours(0, 0, 0, 0);
      this.dateRange.startDate = startDate.toISOString();
    },
    onDateRangeChange(dateRange) {
      this.dateRange = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      this.fetchStats();
    },
    async fetchStats() {
      if (!isRangeInPirsch(this.dateRange.endDate)) {
        this.globalStats = getGlobalStats(null, 0);
        this.readsPerDayObject = [];
        this.readsPerDayKey += 1;
        return;
      }

      this.$apollo.mutate({
        mutation: SetLoadingIndicator,
        variables: { isIndicatorLoading: true },
      });

      const opts = {
        pirschCode: this.pirschCode,
        startDate: this.dateRange.startDate,
        endDate: this.dateRange.endDate,
        pattern: '/',
      };

      try {
        try {
          const [totals, sessionDuration] = await Promise.all([
            fetchGlobalStats(this.$apollo, opts),
            fetchSessionDuration(this.$apollo, opts),
          ]);
          this.globalStats = getGlobalStats(totals, sessionDuration);
          this.queryErrors.globalStats = false;
        } catch (error) {
          console.error('Error fetching global stats:', error);
          this.queryErrors.globalStats = true;
        }

        try {
          const reads = await fetchReadsPerDay(this.$apollo, opts);
          this.readsPerDayObject = getReadsPerDay(reads);
          this.readsPerDayKey += 1;
          this.queryErrors.readsPerDayObject = false;
        } catch (error) {
          console.error('Error fetching reads per day:', error);
          this.queryErrors.readsPerDayObject = true;
        }
      } finally {
        this.$apollo.mutate({
          mutation: SetLoadingIndicator,
          variables: { isIndicatorLoading: false },
        });
      }
    },
  },
  filters: {
    timeString: formatTimeString,
  },
};
</script>

<style scoped lang="scss">
.admin-account-analytics {
  .global-stats {
    h5 {
      color: #a9b1b5;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
    }
    div {
      font-size: 1.5rem;
    }
  }

  .query-error {
    color: #dc3545;
  }
}
</style>
